import { useEffect, useRef, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import "./dbr";
import {
    CameraEnhancer,
    CameraView,
    CaptureVisionRouter,
    MultiFrameResultCrossFilter,
    EnumBarcodeFormat,
} from "dynamsoft-barcode-reader-bundle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";

const componentDestroyedErrorMsg = "VideoCapture Component Destroyed";
const INIT_TIMEOUT_MS = 30_000;

class ScannerInitTimeoutError extends Error {
    constructor(step) {
        super(`Scanner init timed out at step: ${step}`);
        this.name = "ScannerInitTimeoutError";
    }
}

function BarcodeScannerComponent({ onError, onScan, height = "70vh" }) {
    const cameraViewContainer = useRef(null);
    const [phase, setPhase] = useState("loading");
    const [errorMsg, setErrorMsg] = useState(null);
    const [retryNonce, setRetryNonce] = useState(0);

    const handleRetry = useCallback(() => {
        setErrorMsg(null);
        setPhase("loading");
        setRetryNonce((n) => n + 1);
    }, []);

    useEffect(() => {
        let resolveInit;
        const pInit = new Promise((r) => { resolveInit = r; });
        let isDestroyed = false;
        let timeoutId;

        let cvRouter;
        let cameraEnhancer;
        let currentStep = "start";

        const setStep = (step) => {
            currentStep = step;
            Sentry.addBreadcrumb({
                category: "scanner",
                message: `init step: ${step}`,
                level: "info",
            });
        };

        const timeoutPromise = new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
                reject(new ScannerInitTimeoutError(currentStep));
            }, INIT_TIMEOUT_MS);
        });

        const initSequence = (async () => {
            setStep("createCameraView");
            const cameraView = await CameraView.createInstance();
            if (isDestroyed) throw Error(componentDestroyedErrorMsg + "1");

            setStep("createCameraEnhancer");
            cameraEnhancer = await CameraEnhancer.createInstance(cameraView);
            if (isDestroyed) throw Error(componentDestroyedErrorMsg + "2");

            if (cameraViewContainer.current) {
                cameraViewContainer.current.append(cameraView.getUIElement());
            } else {
                console.warn("cameraViewContainer.current is null or undefined, cannot append.");
            }

            setStep("createCVRouter");
            cvRouter = await CaptureVisionRouter.createInstance();
            if (isDestroyed) throw Error(componentDestroyedErrorMsg + "3");

            cvRouter.setInput(cameraEnhancer);

            setStep("loadSettings");
            const settings = await cvRouter.getSimplifiedSettings("ReadSingleBarcode");
            settings.barcodeSettings.barcodeFormatIds =
                EnumBarcodeFormat.BF_ONED | EnumBarcodeFormat.BF_QR_CODE;
            await cvRouter.updateSettings("ReadSingleBarcode", settings);

            cvRouter.addResultReceiver({
                onDecodedBarcodesReceived: (result) => {
                    if (!result.barcodeResultItems.length) return;
                    result.barcodeResultItems.forEach((item) => {
                        if (item.text.length >= 10) {
                            onScan(item.text);
                        }
                    });
                },
            });

            const filter = new MultiFrameResultCrossFilter();
            filter.enableResultCrossVerification("barcode", true);
            filter.enableResultDeduplication("barcode", true);
            await cvRouter.addResultFilter(filter);
            if (isDestroyed) throw Error(componentDestroyedErrorMsg + "4");

            setStep("openCamera");
            await cameraEnhancer.open();
            cameraView.setScanLaserVisible(true);
            if (isDestroyed) throw Error(componentDestroyedErrorMsg + "5");

            setStep("startCapturing");
            await cvRouter.startCapturing("ReadSingleBarcode");
            if (isDestroyed) throw Error(componentDestroyedErrorMsg + "6");

            setStep("ready");
        })();

        Promise.race([initSequence, timeoutPromise])
            .then(() => {
                if (!isDestroyed) setPhase("ready");
            })
            .catch((ex) => {
                const errMsg = ex.message || String(ex);
                console.error("Scanner init failed:", errMsg);

                if (errMsg.startsWith(componentDestroyedErrorMsg)) {
                    return;
                }

                Sentry.withScope((scope) => {
                    scope.setTag("component", "dynamsoft-scanner");
                    scope.setTag("init_step", currentStep);
                    scope.setTag("error_type", ex.name || "Error");
                    scope.setExtra("online", typeof navigator !== "undefined" ? navigator.onLine : null);
                    scope.setExtra("userAgent", typeof navigator !== "undefined" ? navigator.userAgent : null);
                    scope.setExtra(
                        "connection",
                        typeof navigator !== "undefined" && navigator.connection
                            ? navigator.connection.effectiveType
                            : null,
                    );
                    Sentry.captureException(ex);
                });

                if (!isDestroyed) {
                    setErrorMsg(errMsg);
                    setPhase("error");
                    onError(ex);
                }
            })
            .finally(() => {
                clearTimeout(timeoutId);
                resolveInit();
            });

        return () => {
            isDestroyed = true;
            clearTimeout(timeoutId);
            pInit.then(() => {
                cvRouter?.dispose();
                cameraEnhancer?.dispose();
            }).catch(() => {});
        };
    }, [retryNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{ width: "100%", height, position: "relative" }}>
            <div
                ref={cameraViewContainer}
                style={{
                    width: "100%",
                    height: "100%",
                    display: phase === "ready" ? "block" : "none",
                }}
            />

            {phase === "loading" && (
                <div style={overlayStyle}>
                    <CircularProgress />
                    <div style={{ marginTop: 12 }}>Načítavam skener…</div>
                </div>
            )}

            {phase === "error" && (
                <div style={overlayStyle}>
                    <div style={{ marginBottom: 12, color: "#c62828" }}>
                        Skener sa nepodarilo načítať.
                    </div>
                    {errorMsg && (
                        <div style={{ marginBottom: 12, fontSize: 12, color: "#666" }}>
                            {errorMsg}
                        </div>
                    )}
                    <Button variant="contained" color="primary" onClick={handleRetry}>
                        Skúsiť znova
                    </Button>
                </div>
            )}
        </div>
    );
}

const overlayStyle = {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 16,
    background: "rgba(255,255,255,0.95)",
};

export default BarcodeScannerComponent;
